import { MenuItem, TextField, Typography } from '@mui/material';
import { CreatedBy, DayDataContext } from '../types';
import { UUID_RE } from '../urlParams';

/** One plain sentence per Created-by option, shown under the select so the tester knows what each option does in Rails and day-punching. */
export const CREATED_BY_HINTS: Record<CreatedBy, string> = {
  admin: 'An admin creates the punches for the employee. Approved at once in Rails and day-punching. is_manual = true, approved_by = the admin.',
  employee_realtime: 'Like the employee pressed the clock button at that time. Approved at once in both systems. is_manual = false, no reason, no device. Rails may refuse it (403) if the employee is not allowed to web-punch.',
  employee: 'Like the employee asked for a past punch in the app. Pending in both systems until a manager approves it. is_manual = true, reason is sent.'
};

interface DialogCommonFieldsProps {
  createdBy: CreatedBy;
  onCreatedByChange: (createdBy: CreatedBy) => void;
  reason: string;
  onReasonChange: (reason: string) => void;
  /** UUID of a specific profile to create the punches as, overriding the auto-picked admin requester. Only rendered/used when createdBy is 'admin'. */
  requestedBy: string;
  onRequestedByChange: (requestedBy: string) => void;
  /** When given, renders the resolved-location line and (for createdBy: admin) the admin-requester line, below the Created-by/Reason fields. Omit for a dialog with no location/context concept. */
  context?: DayDataContext;
}

/** Created-by select, Reason field, the optional Admin profile UUID override and (optionally) the resolved-location/admin-requester lines, shared by the three "add punches" dialogs so their behaviour stays identical. */
export const DialogCommonFields = ({ createdBy, onCreatedByChange, reason, onReasonChange, requestedBy, onRequestedByChange, context }: DialogCommonFieldsProps) => {
  const requestedByInvalid = requestedBy !== '' && !UUID_RE.test(requestedBy);

  return (
    <>
      <TextField label="Created by" select size="small" value={createdBy} onChange={(e) => onCreatedByChange(e.target.value as CreatedBy)} helperText={CREATED_BY_HINTS[createdBy]}>
        <MenuItem value="admin">admin (approved)</MenuItem>
        <MenuItem value="employee_realtime">employee — real-time punch (approved)</MenuItem>
        <MenuItem value="employee">employee — manual request (pending)</MenuItem>
      </TextField>
      {createdBy !== 'employee_realtime' && <TextField label="Reason" size="small" value={reason} onChange={(e) => onReasonChange(e.target.value)} />}
      {createdBy === 'admin' && (
        <TextField
          label="Admin profile UUID (optional)"
          size="small"
          value={requestedBy}
          onChange={(e) => onRequestedByChange(e.target.value)}
          error={requestedByInvalid}
          helperText={requestedByInvalid ? 'not a UUID' : context ? (context.adminRequester ? `Leave empty to use Admin: ${context.adminRequester.fullName}` : 'Required: no employer profile found in this company') : undefined}
        />
      )}
      {context && (
        <Typography variant="body2" color={context.defaultLocation ? 'text.secondary' : 'warning.main'}>
          {context.defaultLocation ? `Location: ${context.defaultLocation.name} (company default)` : 'Warning: this company has no default location — punches will have no location.'}
        </Typography>
      )}
      {context && createdBy === 'admin' && (
        <Typography variant="body2" color={context.adminRequester ? 'text.secondary' : 'warning.main'}>
          {context.adminRequester ? `Admin: ${context.adminRequester.fullName}` : 'Warning: no admin profile found in this company — creation will fail unless you pass a specific profile.'}
        </Typography>
      )}
    </>
  );
};

export default DialogCommonFields;
